import {
  Component,
  EventEmitter,
  Input,
  model,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ColorPickerModule } from 'primeng/colorpicker';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { LicenseService } from '../../../../../services/remote/license.service';
import { NotificationService } from '../../../../../services/shared/notification.service';

@Component({
  selector: 'cognos-license-details-assistants',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    CheckboxModule,
    TableModule,
    TagModule,
    FloatLabelModule,
    ColorPickerModule,
    InputNumberModule,
    InputTextModule,
    TextareaModule,
    DividerModule,
    TooltipModule
],
  templateUrl: './license-details-assistants.component.html',
  styleUrl: './license-details-assistants.component.scss',
})
export class LicenseDetailsAssistantsComponent {
  @Input() assistantSettings: any;
  @Input() externalEditingAssistant?: any;
  @Input({ required: true }) licenseId!: string;
  public filterText: string = '';
  @Output() openAssistantEdit = new EventEmitter<any>();
  @Output() closeAssistantEdit = new EventEmitter<void>();

  public assistants: any[] = [];

  public formGroup!: FormGroup;
  public isLoading = false;

  public get filteredAssistants(): any[] {
    const q = (this.filterText || '').trim().toLowerCase();
    if (!q) return this.assistants;
    return this.assistants.filter((a) => {
      const name = (a?.name || a?.label || a?.type || '')
        .toString()
        .toLowerCase();
      return name.includes(q);
    });
  }

  public editingAssistant: any | null = null;

  constructor(
    private fb: FormBuilder,
    private _licenseService: LicenseService,
    private _notificationService: NotificationService
  ) {
    this.formGroup = this.fb.group({
      FAQs: [''],
      name: [''],
      shortDescription: [''],
      longDescription: [''],
      color: [''],
      modelName: [''],
      modelTopK: [5],
      modelTopP: [1.0],
      temperature: [0.7],
      topK: [5],
      memoryMessages: [10],
      prompt: [''],
      showFAQs: [true],
      safeguard: [''],
      showThumbs: [true],
      showContext: [true],
      showSources: [true],
      welcomeMessage: [''],
      showWelcomeMessage: [true],
    });
  }

  public startEdit(assistant: any) {
    this.editingAssistant = assistant;
    // Populate form with assistant content
    const content = assistant?.content || assistant || {};

    const faqsText = Array.isArray(content.FAQs) ? content.FAQs.join('\n') : '';

    this.formGroup.patchValue({
      FAQs: faqsText,
      name:
        content.name ??
        content.label ??
        assistant?.name ??
        assistant?.label ??
        '',
      shortDescription:
        content.shortDescription ?? content.shortDescription ?? '',
      longDescription: content.longDescription ?? content.longDescription ?? '',
      color: content.color ?? '',
      modelName: content.modelName ?? '',
      modelTopK: content.modelTopK ?? 5,
      modelTopP: content.modelTopP ?? 1.0,
      temperature: content.temperature ?? 0.7,
      topK: content.topK ?? 5,
      memoryMessages: content.memoryMessages ?? 10,
      prompt: content.prompt ?? '',
      showFAQs: content.showFAQs ?? true,
      safeguard: content.safeguard ?? '',
      showThumbs: content.showThumbs ?? true,
      showContext: content.showContext ?? true,
      showSources: content.showSources ?? true,
      welcomeMessage: content.welcomeMessage ?? '',
      showWelcomeMessage: content.showWelcomeMessage ?? true,
    });

    this.openAssistantEdit.emit(assistant);
  }

  public goBack() {
    this.editingAssistant = null;
    this.closeAssistantEdit.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['externalEditingAssistant'] && this.externalEditingAssistant) {
      // If the parent provides an external editing assistant, use it
      this.editingAssistant = this.externalEditingAssistant;
      // populate form with provided assistant
      if (this.editingAssistant) {
        this.startEdit(this.editingAssistant);
      }
    }
    if (changes['assistantSettings'] && this.assistantSettings) {
      this.assistants = Object.values(this.assistantSettings)
        .filter(
          (setting: any) =>
            setting.type && setting.type.startsWith('assistant-')
        )
        .map((setting: any) => {
          const content = setting?.content || {};

          return {
            ...setting,
            name:
              content?.name ||
              content?.label ||
              setting?.name ||
              setting?.label,
          };
        });
    }
  }

  public isSaveDisabled(): boolean {
    return this.isLoading || this.formGroup.invalid;
  }

  public onSaveClick(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsDirty();
      });

      const errors = this.getFormErrors();
      if (errors.length > 0) {
        this._notificationService.showErrorNotification(
          `Please fix the following errors: ${errors.join(', ')}`
        );
      } else {
        this._notificationService.showErrorNotification(
          'Please check the form fields.'
        );
      }
      return;
    }

    this._updateSettings(this.formGroup);
  }

  private getFormErrors(): string[] {
    const errors: string[] = [];

    Object.keys(this.formGroup.controls).forEach((key) => {
      const control = this.formGroup.get(key);
      if (control?.errors) {
        Object.keys(control.errors).forEach((errorKey) => {
          const errorValue = control.errors![errorKey];
          switch (errorKey) {
            case 'required':
              errors.push(`${key} is required`);
              break;
            case 'min':
              if (
                errorValue &&
                typeof errorValue === 'object' &&
                'min' in errorValue
              ) {
                errors.push(`${key} must be at least ${errorValue.min}`);
              } else {
                errors.push(`${key} must be at least the minimum value`);
              }
              break;
            case 'max':
              if (
                errorValue &&
                typeof errorValue === 'object' &&
                'max' in errorValue
              ) {
                errors.push(`${key} must be at most ${errorValue.max}`);
              } else {
                errors.push(`${key} must be at most the maximum value`);
              }
              break;
            case 'pattern':
              errors.push(`${key} has an invalid format`);
              break;
            default:
              errors.push(`${key} is invalid`);
          }
        });
      }
    });

    return errors;
  }

  private async _updateSettings(formGroup: FormGroup) {
    if (this.isLoading || !this.editingAssistant) return;

    this.isLoading = true;

    try {
      // Use the specific assistant type if available, fallback to 'assistant'
      const typeValue = this.editingAssistant?.type || 'assistant';

      const currentContent =
        this.editingAssistant?.content || this.editingAssistant || {};

      const faqsText = formGroup.get('FAQs')?.value || '';
      const faqsArray = faqsText.trim()
        ? faqsText.split('\n').filter((faq: string) => faq.trim())
        : [];

      const updatedContent = {
        ...currentContent,
        name: formGroup.get('name')?.value,
        shortDescription: formGroup.get('shortDescription')?.value,
        longDescription: formGroup.get('longDescription')?.value,
        color: formGroup.get('color')?.value,
        modelName: formGroup.get('modelName')?.value,
        modelTopK: formGroup.get('modelTopK')?.value,
        modelTopP: formGroup.get('modelTopP')?.value,
        temperature: formGroup.get('temperature')?.value,
        topK: formGroup.get('topK')?.value,
        memoryMessages: formGroup.get('memoryMessages')?.value,
        prompt: formGroup.get('prompt')?.value,
        FAQs: faqsArray,
        showFAQs: formGroup.get('showFAQs')?.value,
        safeguard: formGroup.get('safeguard')?.value,
        showThumbs: formGroup.get('showThumbs')?.value,
        showContext: formGroup.get('showContext')?.value,
        showSources: formGroup.get('showSources')?.value,
        welcomeMessage: formGroup.get('welcomeMessage')?.value,
        showWelcomeMessage: formGroup.get('showWelcomeMessage')?.value,
      };

      const updateResult = await this._licenseService.updateSettings(
        updatedContent,
        typeValue,
        this.licenseId
      );

      if (updateResult) {
        this._notificationService.showSuccessNotification(
          'Assistant settings updated successfully!'
        );
        // Refresh settings to reflect changes
        this._licenseService.clearSettingsCache(this.licenseId);
        await this._licenseService.fetchLicenseSettings(this.licenseId);
      }
    } catch (error: any) {
      console.error('Error updating assistant settings:', error);
      this._notificationService.showErrorNotification(
        error?.error?.detail || 'Failed to update assistant settings'
      );
    } finally {
      this.isLoading = false;
    }
  }
}
